import React, { useEffect, useState } from 'react'
import { CAlert, CBadge, CButton, CFormCheck, CFormInput, CFormLabel, CFormSelect, CFormTextarea } from '@coreui/react'
import { useBostonState, emit } from '../lib/useBoston'
import { useUI } from '../../../shared/ui/UIProvider'
import { readSession } from '../../../services/session'
import {
  biaCreateKnowledge,
  biaDeactivateKnowledge,
  biaDeleteKnowledge,
  biaListKnowledge,
  biaUpdateKnowledge,
} from '../lib/biaApi'

const AGENT_OPTIONS = ['agent1_sst', 'agent2_ssc', 'agent3_gen', 'agent4_scriptlogic']

const __KB_STATE__ = {
  items: [],
  loaded: false,
  selected: null,
  adding: false,
  editing: null,
  query: '',
  category: '',
}

function currentUser() {
  return readSession().email || 'front'
}

function makeUUID() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  return 'kb_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8)
}

function parseTags(raw) {
  if (!raw) return {}
  if (typeof raw === 'object') return raw
  try { return JSON.parse(raw) } catch (_) {}
  return { tags: String(raw).split(',').map((x) => x.trim()).filter(Boolean) }
}

function tagsText(tags) {
  if (!tags || typeof tags !== 'object') return ''
  return JSON.stringify(tags, null, 2)
}

function splitLines(value) {
  return String(value || '').split('\n').map((x) => x.trim()).filter(Boolean)
}

function knowledgeTitle(text) {
  return String(text || '').replace(/^[-*]\s*/, '').slice(0, 72) || 'Particularidad'
}

function normalizeKnowledge(row) {
  const tags = parseTags(row.tags)
  return {
    ...row,
    uuid: row.uuid || row.id,
    title: row.title || row.uuid || 'Knowledge',
    category: row.category || 'general',
    content: row.content || '',
    status: Number(row.status ?? 1),
    tags,
    agents: Array.isArray(tags.agents) ? tags.agents : [],
    patterns: Array.isArray(tags.patterns) ? tags.patterns : [],
    priority: tags.priority || 'normal',
  }
}

async function loadKnowledgeBase() {
  try {
    const rows = await biaListKnowledge()
    __KB_STATE__.items = rows.map(normalizeKnowledge)
  } catch (error) {
    console.error('[KB] error cargando knowledge:', error)
    __KB_STATE__.items = []
  }
}

function kbMdMini(text) {
  return String(text || '')
    .split(/(`[^`]+`|\*\*[^*]+\*\*)/g)
    .map((seg, i) => {
      if (seg.length > 2 && seg.startsWith('`') && seg.endsWith('`')) {
        return (
          <code key={i} className="bia-mono" style={{ background: 'var(--bg)', padding: '1px 5px', borderRadius: 3, fontSize: '11.5px' }}>
            {seg.slice(1, -1)}
          </code>
        )
      }
      if (seg.length > 4 && seg.startsWith('**') && seg.endsWith('**')) return <b key={i}>{seg.slice(2, -2)}</b>
      return seg
    })
}

const lecQueue = {
  KEY: 'boston_kb_lecciones_v1',
  all() {
    try { return JSON.parse(localStorage.getItem(this.KEY) || '[]') } catch (_) { return [] }
  },
  saveAll(items) {
    localStorage.setItem(this.KEY, JSON.stringify(items))
  },
  add(lesson) {
    const items = this.all()
    lesson.id = 'lec_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6)
    lesson.ts = new Date().toISOString()
    lesson.status = 'pendiente'
    items.push(lesson)
    this.saveAll(items)
    return lesson
  },
  setStatus(id, status) {
    const items = this.all()
    const item = items.find((x) => x.id === id)
    if (item) {
      item.status = status
      this.saveAll(items)
    }
  },
  remove(id) {
    this.saveAll(this.all().filter((x) => x.id !== id))
  },
  approved() {
    return this.all().filter((x) => x.status === 'aprobada')
  },
}

if (typeof window !== 'undefined') window.lecQueue = lecQueue

function lecPill(status) {
  if (status === 'aprobada') return <span className="bia-badge ok">aprobada</span>
  if (status === 'pendiente') return <span className="bia-badge warn">pendiente de OK</span>
  return <span className="bia-pill mini">{status}</span>
}

function KBAddForm({ editing, onCancel, onSaved }) {
  const { toast } = useUI()
  const item = editing || {}
  const [form, setForm] = useState({
    uuid: item.uuid || makeUUID(),
    title: item.title || '',
    category: item.category || 'general',
    agents: Array.isArray(item.agents) ? item.agents : [],
    priority: item.priority || 'normal',
    patterns: (item.patterns || []).join('\n'),
    content: item.content || '',
    status: String(item.status ?? 1),
    tagsJson: tagsText(item.tags),
  })
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }))

  async function save() {
    const uuid = form.uuid.trim()
    const title = form.title.trim()
    const content = form.content.trim()
    if (!uuid || !title || !content) {
      setMessage('UUID, titulo y contenido son obligatorios')
      return
    }

    let tags
    try {
      tags = form.tagsJson.trim() ? JSON.parse(form.tagsJson) : {}
    } catch (error) {
      setMessage('Tags JSON invalido: ' + error.message)
      return
    }

    tags = {
      ...tags,
      agents: Array.isArray(form.agents) ? form.agents : [],
      priority: form.priority || 'normal',
      patterns: splitLines(form.patterns),
    }

    const payload = {
      uuid,
      title,
      category: form.category.trim() || 'general',
      content,
      tags,
      status: Number(form.status || 0),
      createdUser: item.createdUser || currentUser(),
      updatedUser: currentUser(),
    }

    setSaving(true)
    try {
      if (editing) await biaUpdateKnowledge(uuid, payload)
      else await biaCreateKnowledge(payload)
      toast(editing ? 'Knowledge actualizado' : 'Knowledge creado', 'success')
      await onSaved(uuid)
    } catch (error) {
      setMessage(error.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card-x">
      <h3>{editing ? 'Editar knowledge' : 'Agregar knowledge'}</h3>
      {message ? <CAlert color="warning">{message}</CAlert> : null}
      <div className="mb-2">
        <CFormLabel>UUID</CFormLabel>
        <CFormInput className="bia-mono" value={form.uuid} disabled={Boolean(editing)} onChange={(event) => set('uuid', event.target.value)} />
      </div>
      <div className="mb-2">
        <CFormLabel>Titulo</CFormLabel>
        <CFormInput value={form.title} onChange={(event) => set('title', event.target.value)} />
      </div>
      <div className="bia-grid-2 mb-2">
        <div>
          <CFormLabel>Categoria</CFormLabel>
          <CFormInput value={form.category} onChange={(event) => set('category', event.target.value)} />
        </div>
        <div>
          <CFormLabel>Status</CFormLabel>
          <CFormSelect value={form.status} onChange={(event) => set('status', event.target.value)}>
            <option value="1">activo</option>
            <option value="0">inactivo</option>
          </CFormSelect>
        </div>
      </div>
      <div className="bia-grid-2 mb-2">
        <div>
          <CFormLabel>Agentes</CFormLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', alignItems: 'center', paddingTop: 4 }}>
            <CFormCheck
              id="ag-todos"
              label="Todos"
              checked={form.agents.length === 0}
              onChange={() => set('agents', [])}
            />
            {AGENT_OPTIONS.map((a) => (
              <CFormCheck
                key={a}
                id={'ag-' + a}
                label={a}
                checked={form.agents.includes(a)}
                onChange={(event) => set('agents', event.target.checked
                  ? [...form.agents, a]
                  : form.agents.filter((x) => x !== a))}
              />
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>&quot;Todos&quot; (ninguno tildado) = la usan los 4 agentes.</div>
        </div>
        <div>
          <CFormLabel>Prioridad</CFormLabel>
          <CFormSelect value={form.priority} onChange={(event) => set('priority', event.target.value)}>
            <option value="normal">normal</option>
            <option value="high">high</option>
          </CFormSelect>
        </div>
      </div>
      <div className="mb-2">
        <CFormLabel>Patrones (uno por linea)</CFormLabel>
        <CFormTextarea rows={3} value={form.patterns} onChange={(event) => set('patterns', event.target.value)} />
      </div>
      <div className="mb-2">
        <CFormLabel>Contenido</CFormLabel>
        <CFormTextarea className="bia-mono" rows={12} value={form.content} onChange={(event) => set('content', event.target.value)} />
      </div>
      <div className="mb-2">
        <CFormLabel>Tags JSON extra</CFormLabel>
        <CFormTextarea className="bia-mono" rows={5} value={form.tagsJson} onChange={(event) => set('tagsJson', event.target.value)} />
      </div>
      <div className="bia-row-input" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
        <CButton color="light" onClick={onCancel} disabled={saving}>Cancelar</CButton>
        <CButton color="success" className="btn-brand" onClick={save} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar'}
        </CButton>
      </div>
    </div>
  )
}

function AiKnowledgeView() {
  useBostonState()
  const { toast, confirm } = useUI()
  const [partAddText, setPartAddText] = useState('')
  const [partAddSec, setPartAddSec] = useState('general')

  useEffect(() => {
    if (!__KB_STATE__.loaded) {
      __KB_STATE__.loaded = true
      loadKnowledgeBase().then(() => emit())
    }
  }, [])

  const allItems = __KB_STATE__.items || []
  const query = (__KB_STATE__.query || '').toLowerCase()
  const categories = Array.from(new Set(allItems.map((item) => item.category || 'general'))).sort()
  const visibleItems = allItems.filter((item) => {
    const matchesQuery = !query || [item.title, item.uuid, item.category, item.content].join(' ').toLowerCase().includes(query)
    const matchesCategory = !__KB_STATE__.category || item.category === __KB_STATE__.category
    return matchesQuery && matchesCategory
  })
  const particularidades = allItems.filter((item) => String(item.category || '').toLowerCase().includes('particular'))

  async function refresh() {
    await loadKnowledgeBase()
    emit()
  }

  function addNew() {
    __KB_STATE__.adding = true
    __KB_STATE__.editing = null
    __KB_STATE__.selected = null
    emit()
  }

  function cancelForm() {
    __KB_STATE__.adding = false
    __KB_STATE__.editing = null
    emit()
  }

  async function saved(uuid) {
    __KB_STATE__.adding = false
    __KB_STATE__.editing = null
    await refresh()
    __KB_STATE__.selected = uuid
    emit()
  }

  function selectItem(uuid) {
    __KB_STATE__.selected = uuid
    __KB_STATE__.adding = false
    emit()
  }

  function editItem(uuid) {
    const item = allItems.find((x) => x.uuid === uuid)
    if (!item) return
    __KB_STATE__.editing = item
    __KB_STATE__.adding = true
    emit()
  }

  async function activateItem(item) {
    await biaUpdateKnowledge(item.uuid, { ...item, status: 1, updatedUser: currentUser() })
    toast('Knowledge activado', 'success')
    await refresh()
  }

  async function deactivateItem(item) {
    await biaDeactivateKnowledge(item.uuid, currentUser())
    toast('Knowledge desactivado', 'success')
    await refresh()
  }

  async function deleteItem(item) {
    const ok = await confirm({
      title: 'Eliminar knowledge',
      body: `Vas a eliminar "${item.title}".`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      tone: 'danger',
    })
    if (!ok) return
    await biaDeleteKnowledge(item.uuid)
    toast('Knowledge eliminado', 'success')
    __KB_STATE__.selected = null
    await refresh()
  }

  async function partAdd() {
    const text = partAddText.trim()
    if (!text) {
      toast('Escribi la particularidad.', 'warning')
      return
    }
    const uuid = makeUUID()
    await biaCreateKnowledge({
      uuid,
      title: knowledgeTitle(text),
      category: 'particularidades',
      content: text,
      tags: { kind: 'particularidad', section: partAddSec || 'general', agents: [] },
      status: 1,
      createdUser: currentUser(),
      updatedUser: currentUser(),
    })
    setPartAddText('')
    toast('Particularidad guardada', 'success')
    await refresh()
  }

  async function partDel(item) {
    await deactivateItem(item)
  }

  function lecOk(id) {
    lecQueue.setStatus(id, 'aprobada')
    emit()
  }

  function lecDel(id) {
    lecQueue.remove(id)
    emit()
  }

  function renderDetail(uuid) {
    const item = allItems.find((x) => x.uuid === uuid)
    if (!item) {
      return (
        <div className="card-x">
          <p className="bia-empty">No encontrado.</p>
        </div>
      )
    }
    return (
      <div className="card-x">
        <div className="bia-card-head">
          <h3>{item.title}</h3>
          <span className="bia-pill mini">{item.uuid}</span>
        </div>
        <div style={{ display: 'flex', gap: 6, margin: '10px 0 12px', flexWrap: 'wrap' }}>
          <span className="bia-pill mini">Categoria: {item.category}</span>
          <span className="bia-pill mini">Prioridad: {item.priority}</span>
          <CBadge color={item.status === 1 ? 'success' : 'secondary'}>{item.status === 1 ? 'activo' : 'inactivo'}</CBadge>
          {item.agents.map((agent) => <span key={agent} className="bia-pill mini">{agent}</span>)}
        </div>
        {item.patterns.length ? (
          <div style={{ marginBottom: 12 }}>
            <b style={{ fontSize: 12 }}>Patrones:</b>
            <ul style={{ margin: '4px 0 0', fontSize: 13 }}>
              {item.patterns.map((pattern, index) => <li key={index}>{pattern}</li>)}
            </ul>
          </div>
        ) : null}
        <pre className="bia-pre" style={{ maxHeight: 440, overflow: 'auto', fontSize: 12 }}>{item.content}</pre>
        <div className="bia-row-input" style={{ marginTop: 12, flexWrap: 'wrap' }}>
          {item.status !== 1 ? <CButton color="success" className="btn-brand" onClick={() => activateItem(item)}>Activar</CButton> : null}
          {item.status === 1 ? <CButton color="warning" variant="outline" onClick={() => deactivateItem(item)}>Desactivar</CButton> : null}
          <CButton color="light" onClick={() => editItem(item.uuid)}>Editar</CButton>
          <CButton color="danger" variant="outline" onClick={() => deleteItem(item)}>Eliminar</CButton>
        </div>
      </div>
    )
  }

  const lecs = lecQueue.all().slice().reverse()
  const pendingLessons = lecs.filter((x) => x.status === 'pendiente').length

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Base de Conocimiento</h1>
          <p>Prompts y knowledge se administran desde DB/API via public-api-boston /admin/ai.</p>
        </div>
        <div className="bia-row-input">
          <CButton color="light" onClick={refresh}>Recargar</CButton>
          <CButton color="success" className="btn-brand" onClick={addNew}>Agregar knowledge</CButton>
        </div>
      </div>

      <div className="card-x">
        <div className="bia-card-head" style={{ flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Particularidades ({particularidades.length})</h3>
          <CFormInput
            size="sm"
            style={{ maxWidth: 260 }}
            placeholder="Buscar knowledge"
            value={__KB_STATE__.query || ''}
            onChange={(event) => {
              __KB_STATE__.query = event.target.value
              emit()
            }}
          />
        </div>
        <div className="bia-row-input" style={{ flexWrap: 'wrap', padding: '10px 0', borderBottom: '2px solid var(--border)', marginTop: 8 }}>
          <CFormInput size="sm" style={{ maxWidth: 220 }} placeholder="Seccion" value={partAddSec} onChange={(event) => setPartAddSec(event.target.value)} />
          <CFormInput size="sm" style={{ flex: 1, minWidth: 260 }} placeholder="Nueva particularidad" value={partAddText} onChange={(event) => setPartAddText(event.target.value)} />
          <CButton size="sm" color="success" className="btn-brand" onClick={partAdd}>Agregar</CButton>
        </div>
        <div className="bia-scroll" style={{ maxHeight: 340 }}>
          {particularidades.length ? particularidades.map((item) => (
            <div key={item.uuid} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: 1, fontSize: '12.3px', lineHeight: 1.45 }}>{kbMdMini(item.content)}</div>
              <CButton size="sm" color="light" title="Desactivar particularidad" style={{ flex: 'none' }} onClick={() => partDel(item)}>Desactivar</CButton>
            </div>
          )) : <p className="bia-empty" style={{ padding: 18 }}>No hay particularidades en DB.</p>}
        </div>
      </div>

      <div className="bia-grid-2asym">
        <div className="card-x">
          <div className="bia-card-head" style={{ gap: 10, flexWrap: 'wrap' }}>
            <h3>Knowledge ({visibleItems.length})</h3>
            <CFormSelect
              size="sm"
              style={{ maxWidth: 220 }}
              value={__KB_STATE__.category || ''}
              onChange={(event) => {
                __KB_STATE__.category = event.target.value
                emit()
              }}
            >
              <option value="">Todas las categorias</option>
              {categories.map((category) => <option key={category} value={category}>{category}</option>)}
            </CFormSelect>
          </div>
          <div className="bia-scroll" style={{ maxHeight: 600 }}>
            {visibleItems.length === 0 ? (
              <p className="bia-empty" style={{ padding: '24px 0' }}>No hay knowledge para mostrar.</p>
            ) : visibleItems.map((item) => (
              <div
                key={item.uuid}
                onClick={() => selectItem(item.uuid)}
                style={{
                  padding: '12px 8px',
                  borderBottom: '1px solid var(--border)',
                  cursor: 'pointer',
                  borderRadius: 8,
                  background: __KB_STATE__.selected === item.uuid ? 'var(--brand-soft)' : 'transparent',
                }}
              >
                <div style={{ fontWeight: 600 }}>{item.title}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                  <span className="bia-pill mini">{item.category}</span>
                  <CBadge color={item.status === 1 ? 'success' : 'secondary'}>{item.status === 1 ? 'activo' : 'inactivo'}</CBadge>
                  {item.priority === 'high' ? <span className="bia-badge warn">prioritario</span> : null}
                  {item.agents.map((agent) => <span key={agent} className="bia-pill mini">{agent}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          {__KB_STATE__.adding ? (
            <KBAddForm
              key={(__KB_STATE__.editing && __KB_STATE__.editing.uuid) || 'nuevo'}
              editing={__KB_STATE__.editing}
              onCancel={cancelForm}
              onSaved={saved}
            />
          ) : __KB_STATE__.selected ? (
            renderDetail(__KB_STATE__.selected)
          ) : (
            <div className="card-x">
              <p className="bia-empty">Selecciona un registro de la izquierda o agrega uno nuevo.</p>
            </div>
          )}
        </div>
      </div>

      <div className="card-x" style={{ marginTop: 18 }}>
        <h3>Lecciones aprendidas ({lecs.length}{pendingLessons ? ` - ${pendingLessons} por aprobar` : ''})</h3>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--muted)' }}>
          Se mantienen en localStorage por compatibilidad. Las aprobadas se inyectan junto con knowledge de DB.
        </p>
        <div className="bia-scroll" style={{ maxHeight: 340, marginTop: 8 }}>
          {lecs.length === 0 ? (
            <p className="bia-empty" style={{ padding: '20px 0' }}>Todavia no hay lecciones.</p>
          ) : lecs.map((lesson) => (
            <div key={lesson.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <span className={`bia-badge ${lesson.origen === 'dev' ? 'warn' : 'ok'}`}>{lesson.origen === 'dev' ? 'dev' : 'chat'}</span>{' '}
                  <strong style={{ fontSize: '12.5px' }}>{lesson.script || '(script)'}</strong> {lecPill(lesson.status)}
                </div>
                <div className="bia-row-input">
                  {lesson.status !== 'aprobada' ? <CButton size="sm" color="success" className="btn-brand" onClick={() => lecOk(lesson.id)}>OK</CButton> : null}
                  <CButton size="sm" color="light" onClick={() => lecDel(lesson.id)}>Eliminar</CButton>
                </div>
              </div>
              <div style={{ fontSize: 12, marginTop: 5 }}><b>Indicacion:</b> {lesson.instruccion || ''}</div>
              <div style={{ fontSize: 12, marginTop: 2, color: 'var(--muted)' }}><b>Resolucion:</b> <code style={{ fontSize: 11 }}>{lesson.resolucion || ''}</code></div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export default AiKnowledgeView
